import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Fade,
  Skeleton,
  Alert,
  Button,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  FilterList,
  Download,
  Refresh,
  InfoOutlined,
  ErrorOutline,
  Lightbulb,
  CheckCircle,
  Warning,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

import FilterPanel from '../components/analytics/FilterPanel';
import MetricCard from '../components/analytics/MetricCard';
import QuickReportTemplates from '../components/analytics/QuickReportTemplates';
import { hybridApi } from '../services/api';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { glassStyle } from '../theme/luxuryTheme';

interface FilterState {
  selectedStores: string[];
  selectedBrands: string[];
  selectedSources: string[];
  dateRange: {
    startDate: string;
    endDate: string;
    preset: string;
  };
  comparisonMode: 'range' | 'trend' | 'comparison';
  // Advanced filters
  selectedMetrics: string[];
  selectedRevenueCenters: string[];
  selectedMealPeriods: string[];
  selectedPaymentMethods: string[];
  selectedOrderTypes: string[];
  // Trend mode
  trendPeriods: number;
  trendPeriodType: string;
  trendEndDate: string;
  // Comparison mode
  comparisonTypes: {
    daily: boolean;
    wtd: boolean;
    mtd: boolean;
    fptd: boolean;
    ytd: boolean;
  };
  comparisonEndDate: string;
}

interface ChartDataPoint {
  date: string;
  sales: number;
  orders: number;
  aov: number;
}

const AdvancedAnalytics: React.FC = () => {
  const [filters, setFilters] = useState<FilterState>({
    selectedStores: [],
    selectedBrands: [],
    selectedSources: [],
    dateRange: {
      startDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
      preset: 'Yesterday',
    },
    comparisonMode: 'range',
    // Advanced filters - defaults
    selectedMetrics: ['totalSales', 'netSales', 'orderCount', 'averageOrderValue'],
    selectedRevenueCenters: ['all'],
    selectedMealPeriods: ['allDay'],
    selectedPaymentMethods: ['all'],
    selectedOrderTypes: ['all'],
    // Trend mode defaults
    trendPeriods: 7,
    trendPeriodType: 'DAYS',
    trendEndDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
    // Comparison mode defaults
    comparisonTypes: {
      daily: true,
      wtd: true,
      mtd: false,
      fptd: false,
      ytd: false,
    },
    comparisonEndDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
  });

  const [showFilters, setShowFilters] = useState(true);
  const [executedFilters, setExecutedFilters] = useState<FilterState | null>(null);
  const [showTemplates, setShowTemplates] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);

  // Ref for scrolling to filter/results area
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const handleExecuteReport = () => {
    setIsExecuting(true);
    setExecutedFilters({ ...filters });
  };

  const handleTemplateSelect = (template: any) => {
    console.log('Template selected:', template.name);

    // Determine date range based on preset
    let dateRange = { ...filters.dateRange };

    if (template.filters.datePreset) {
      switch (template.filters.datePreset) {
        case 'Yesterday':
          dateRange = {
            startDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            preset: 'Yesterday',
          };
          break;
        case 'WTD':
          dateRange = {
            startDate: format(subDays(new Date(), new Date().getDay()), 'yyyy-MM-dd'),
            endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            preset: 'WTD',
          };
          break;
        case 'MTD':
          dateRange = {
            startDate: format(new Date(new Date().getFullYear(), new Date().getMonth(), 1), 'yyyy-MM-dd'),
            endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            preset: 'MTD',
          };
          break;
        default:
          dateRange = {
            startDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            endDate: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
            preset: template.filters.datePreset,
          };
      }
    }

    // Apply template filters
    const newFilters = {
      ...filters,
      comparisonMode: template.filters.comparisonMode,
      dateRange,
    };

    console.log('New filters:', newFilters);
    console.log('Date range:', dateRange);

    setFilters(newFilters);
    setIsExecuting(true);

    // Auto-execute immediately
    setExecutedFilters({ ...newFilters });
    console.log('Executed filters set');
  };

  const handleExportData = () => {
    if (!analyticsData || chartData.length === 0) {
      alert('No data available to export');
      return;
    }

    try {
      // Prepare CSV data
      const csvData = chartData.map((row) => ({
        Date: row.date,
        Sales: row.sales.toFixed(2),
        Orders: row.orders,
        'Avg Order Value': row.aov.toFixed(2),
      }));

      // Add summary row
      if (metrics) {
        csvData.push({
          Date: 'TOTAL',
          Sales: metrics.totalSales.toFixed(2),
          Orders: metrics.totalOrders,
          'Avg Order Value': metrics.avgOrderValue.toFixed(2),
        });
      }

      // Convert to CSV
      const headers = Object.keys(csvData[0]).join(',');
      const rows = csvData.map((row) => Object.values(row).join(',')).join('\n');
      const csv = `${headers}\n${rows}`;

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const startDate = executedFilters?.dateRange.startDate || format(new Date(), 'yyyy-MM-dd');
      const endDate = executedFilters?.dateRange.endDate || format(new Date(), 'yyyy-MM-dd');
      a.download = `sales-report-${startDate}-to-${endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  // Fetch stores
  const { data: storesData } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await hybridApi.getStores();
      return response.data;
    },
  });

  // Fetch analytics data based on filters
  const { data: analyticsData, isLoading, error: analyticsError, refetch } = useQuery({
    queryKey: ['advanced-analytics', executedFilters],
    queryFn: async () => {
      console.log('Query function called, executedFilters:', executedFilters);
      if (!executedFilters) return null;

      const { startDate, endDate } = executedFilters.dateRange;
      const { selectedStores, comparisonMode } = executedFilters;

      console.log('Comparison mode:', comparisonMode);
      console.log('Fetching data for date range:', startDate, 'to', endDate);
      console.log('Selected stores:', selectedStores);

      // Prepare filter parameters including selected metrics
      const advancedFilters = {
        revenueCenters: executedFilters.selectedRevenueCenters,
        mealPeriods: executedFilters.selectedMealPeriods,
        paymentMethods: executedFilters.selectedPaymentMethods,
        orderTypes: executedFilters.selectedOrderTypes,
        metrics: executedFilters.selectedMetrics,
      };

      // Handle Trend Mode
      if (comparisonMode === 'trend') {
        console.log('Trend mode - generating multiple periods');
        const { trendPeriods, trendPeriodType, trendEndDate } = executedFilters;

        // Generate date ranges for each period
        const periods = [];
        const end = new Date(trendEndDate);

        for (let i = 0; i < trendPeriods; i++) {
          let periodStart, periodEnd;

          if (trendPeriodType === 'DAYS') {
            periodEnd = subDays(end, i);
            periodStart = periodEnd;
          } else if (trendPeriodType === 'WEEKS') {
            periodEnd = subDays(end, i * 7);
            periodStart = subDays(periodEnd, 6);
          } else if (trendPeriodType === 'MONTHS') {
            periodEnd = new Date(end.getFullYear(), end.getMonth() - i, end.getDate());
            periodStart = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1);
          } else {
            // Default to days
            periodEnd = subDays(end, i);
            periodStart = periodEnd;
          }

          periods.push({
            startDate: format(periodStart, 'yyyy-MM-dd'),
            endDate: format(periodEnd, 'yyyy-MM-dd'),
            label: format(periodEnd, 'MMM dd, yyyy')
          });
        }

        periods.reverse(); // Show oldest to newest

        // Fetch data for each period
        const periodDataPromises = periods.map(async (period) => {
          try {
            const storeIds = selectedStores.length > 0 ? selectedStores : undefined;
            const storeId = storeIds ? storeIds[0] : undefined; // For now, use first store or all

            const response = await hybridApi.getSalesRange(
              period.startDate,
              period.endDate,
              storeId,
              advancedFilters
            );

            return {
              ...period,
              data: response.data
            };
          } catch (err) {
            console.error(`Error fetching trend data for ${period.label}:`, err);
            return {
              ...period,
              data: {
                summary: { totalSales: 0, totalOrders: 0, averageOrderValue: 0 },
                dailyBreakdown: []
              }
            };
          }
        });

        const periodData = await Promise.all(periodDataPromises);

        // Aggregate all periods for chart
        const allDaily = periodData.flatMap(p =>
          (p.data.dailyBreakdown || []).map((d: any) => ({
            date: d.date,
            sales: d.sales || 0,
            orders: d.orders || 0,
            averageOrderValue: d.averageOrderValue || 0
          }))
        );

        // Calculate totals
        const totalSales = periodData.reduce((sum, p) => sum + (p.data.summary?.totalSales || 0), 0);
        const totalOrders = periodData.reduce((sum, p) => sum + (p.data.summary?.totalOrders || 0), 0);

        return {
          startDate: periods[0].startDate,
          endDate: periods[periods.length - 1].endDate,
          mode: 'trend',
          periods: periodData,
          summary: {
            totalSales,
            totalOrders,
            averageOrderValue: totalOrders > 0 ? totalSales / totalOrders : 0,
            averageDailySales: periods.length > 0 ? totalSales / periods.length : 0
          },
          dailyBreakdown: allDaily
        };
      }

      // Handle Comparison Mode
      if (comparisonMode === 'comparison') {
        console.log('Comparison mode - fetching comparisons');
        const { comparisonTypes, comparisonEndDate } = executedFilters;
        const endDate = new Date(comparisonEndDate);
        const comparisons = [];

        // Helper to calculate dates and fetch comparison
        const fetchComparison = async (label: string, currentStart: Date, currentEnd: Date) => {
          const currentStartStr = format(currentStart, 'yyyy-MM-dd');
          const currentEndStr = format(currentEnd, 'yyyy-MM-dd');

          // Calculate last year dates (365 days prior)
          const lastYearStart = subDays(currentStart, 365);
          const lastYearEnd = subDays(currentEnd, 365);
          const lastYearStartStr = format(lastYearStart, 'yyyy-MM-dd');
          const lastYearEndStr = format(lastYearEnd, 'yyyy-MM-dd');

          try {
            const storeId = selectedStores.length > 0 ? selectedStores[0] : undefined;

            const [currentData, lastYearData] = await Promise.all([
              hybridApi.getSalesRange(currentStartStr, currentEndStr, storeId, advancedFilters),
              hybridApi.getSalesRange(lastYearStartStr, lastYearEndStr, storeId, advancedFilters)
            ]);

            const currentSummary = currentData.data.summary || {};
            const lastYearSummary = lastYearData.data.summary || {};

            return {
              label,
              currentPeriod: {
                startDate: currentStartStr,
                endDate: currentEndStr,
                ...currentSummary
              },
              lastYearPeriod: {
                startDate: lastYearStartStr,
                endDate: lastYearEndStr,
                ...lastYearSummary
              },
              comparison: {
                salesChange: lastYearSummary.totalSales > 0
                  ? ((currentSummary.totalSales - lastYearSummary.totalSales) / lastYearSummary.totalSales * 100)
                  : 0,
                ordersChange: lastYearSummary.totalOrders > 0
                  ? ((currentSummary.totalOrders - lastYearSummary.totalOrders) / lastYearSummary.totalOrders * 100)
                  : 0,
                salesDiff: currentSummary.totalSales - lastYearSummary.totalSales,
                ordersDiff: currentSummary.totalOrders - lastYearSummary.totalOrders
              }
            };
          } catch (err) {
            console.error(`Error fetching ${label} comparison:`, err);
            return {
              label,
              error: true,
              currentPeriod: {},
              lastYearPeriod: {},
              comparison: { salesChange: 0, ordersChange: 0, salesDiff: 0, ordersDiff: 0 }
            };
          }
        };

        // Fetch requested comparisons in parallel
        const promises = [];

        if (comparisonTypes.daily) {
          // Daily: just the end date
          promises.push(fetchComparison('Daily', endDate, endDate));
        }

        if (comparisonTypes.wtd) {
          // WTD: Start of week (Sunday) to end date
          const weekStart = new Date(endDate);
          weekStart.setDate(endDate.getDate() - endDate.getDay());
          promises.push(fetchComparison('WTD', weekStart, endDate));
        }

        if (comparisonTypes.mtd) {
          // MTD: First of month to end date
          const monthStart = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
          promises.push(fetchComparison('MTD', monthStart, endDate));
        }

        if (comparisonTypes.fptd) {
          // FPTD: Fiscal period (assume 4-week periods starting Jan 1)
          // Simple implementation: start of current 4-week period
          const dayOfYear = Math.floor((endDate.getTime() - new Date(endDate.getFullYear(), 0, 1).getTime()) / (1000 * 60 * 60 * 24));
          const periodStart = new Date(endDate.getFullYear(), 0, 1 + Math.floor(dayOfYear / 28) * 28);
          promises.push(fetchComparison('FPTD', periodStart, endDate));
        }

        if (comparisonTypes.ytd) {
          // YTD: Jan 1 to end date
          const yearStart = new Date(endDate.getFullYear(), 0, 1);
          promises.push(fetchComparison('YTD', yearStart, endDate));
        }

        const comparisonResults = await Promise.all(promises);

        // Use the first comparison for main summary and chart
        const primary = comparisonResults[0];

        return {
          startDate: primary?.currentPeriod?.startDate || comparisonEndDate,
          endDate: primary?.currentPeriod?.endDate || comparisonEndDate,
          mode: 'comparison',
          comparisons: comparisonResults,
          summary: primary?.currentPeriod || {},
          dailyBreakdown: [], // Comparisons don't show daily breakdown
          primaryComparison: primary
        };
      }

      // Handle Range Mode (default)
      // If specific stores are selected, aggregate their data
      if (selectedStores.length > 0) {
        // Fetch data for each store and aggregate
        const storeDataPromises = selectedStores.map(storeId =>
          hybridApi.getSalesRange(startDate, endDate, storeId, advancedFilters)
            .then(res => res.data)
            .catch(err => {
              console.error(`Error fetching data for store ${storeId}:`, err);
              return {
                dailyBreakdown: [],
                summary: { totalSales: 0, totalOrders: 0, averageOrderValue: 0, averageDailySales: 0 }
              };
            })
        );

        const storeDataArray = await Promise.all(storeDataPromises);

        // Aggregate data from all selected stores
        const aggregatedDaily = new Map<string, any>();

        storeDataArray.forEach(storeData => {
          if (storeData.dailyBreakdown) {
            storeData.dailyBreakdown.forEach((day: any) => {
              const existing = aggregatedDaily.get(day.date);
              if (existing) {
                existing.sales += day.sales;
                existing.orders += day.orders;
              } else {
                aggregatedDaily.set(day.date, {
                  date: day.date,
                  sales: day.sales,
                  orders: day.orders,
                  averageOrderValue: 0 // Will recalculate after
                });
              }
            });
          }
        });

        // Convert map to array and calculate AOV
        const dailyBreakdown = Array.from(aggregatedDaily.values()).map(day => ({
          ...day,
          averageOrderValue: day.orders > 0 ? day.sales / day.orders : 0
        })).sort((a, b) => a.date.localeCompare(b.date));

        // Calculate aggregated summary
        const totalSales = dailyBreakdown.reduce((sum, day) => sum + day.sales, 0);
        const totalOrders = dailyBreakdown.reduce((sum, day) => sum + day.orders, 0);

        // Aggregate revenue center breakdown from all stores
        let revenueCenterBreakdown = null;
        const revenueCenterData = storeDataArray
          .map(data => data.revenueCenterBreakdown)
          .filter(breakdown => breakdown != null);

        if (revenueCenterData.length > 0) {
          revenueCenterBreakdown = {
            dineIn: revenueCenterData.reduce((sum, b) => sum + (b.dineIn || 0), 0),
            takeout: revenueCenterData.reduce((sum, b) => sum + (b.takeout || 0), 0),
            delivery: revenueCenterData.reduce((sum, b) => sum + (b.delivery || 0), 0),
            curbside: revenueCenterData.reduce((sum, b) => sum + (b.curbside || 0), 0)
          };
        }

        // Aggregate meal period breakdown from all stores
        let mealPeriodBreakdown: {
          [key: string]: {
            totalSales: number;
            orderCount: number;
          };
        } | null = null;
        const mealPeriodData = storeDataArray
          .map(data => data.mealPeriodBreakdown)
          .filter(breakdown => breakdown != null);

        if (mealPeriodData.length > 0) {
          const periods = ['breakfast', 'lunch', 'dinner', 'lateNight'];
          mealPeriodBreakdown = {};
          periods.forEach(period => {
            const periodData = mealPeriodData.map(b => b[period]).filter(p => p != null);
            if (periodData.length > 0) {
              mealPeriodBreakdown![period] = {
                totalSales: periodData.reduce((sum, p) => sum + (p.totalSales || 0), 0),
                orderCount: periodData.reduce((sum, p) => sum + (p.orderCount || 0), 0)
              };
            }
          });
        }

        return {
          startDate,
          endDate,
          storeId: selectedStores.join(','),
          summary: {
            totalSales,
            totalOrders,
            averageOrderValue: totalOrders > 0 ? totalSales / totalOrders : 0,
            averageDailySales: dailyBreakdown.length > 0 ? totalSales / dailyBreakdown.length : 0
          },
          dailyBreakdown,
          revenueCenterBreakdown,
          mealPeriodBreakdown
        };
      }

      // If no stores selected, get all stores data
      console.log('Fetching all stores data (no specific stores selected)');
      const response = await hybridApi.getSalesRange(startDate, endDate, undefined, advancedFilters);
      console.log('API response:', response.data);
      return response.data;
    },
    enabled: !!executedFilters,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  console.log('Current query state - isLoading:', isLoading, 'isExecuting:', isExecuting, 'analyticsData:', analyticsData, 'error:', analyticsError);

  // Fetch previous period data for comparison
  const { data: previousPeriodData } = useQuery({
    queryKey: ['previous-period', executedFilters],
    queryFn: async () => {
      if (!executedFilters) return null;

      const { startDate, endDate } = executedFilters.dateRange;
      const { selectedStores } = executedFilters;
      const start = new Date(startDate);
      const end = new Date(endDate);
      const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      // Calculate previous period dates
      const prevEndDate = format(subDays(start, 1), 'yyyy-MM-dd');
      const prevStartDate = format(subDays(start, daysDiff), 'yyyy-MM-dd');

      // If specific stores are selected, aggregate their data
      if (selectedStores.length > 0) {
        const storeDataPromises = selectedStores.map(storeId =>
          hybridApi.getSalesRange(prevStartDate, prevEndDate, storeId).then(res => res.data)
        );

        const storeDataArray = await Promise.all(storeDataPromises);

        // Aggregate summary only (we don't need daily breakdown for previous period)
        const totalSales = storeDataArray.reduce((sum, data) => sum + (data.summary?.totalSales || 0), 0);
        const totalOrders = storeDataArray.reduce((sum, data) => sum + (data.summary?.totalOrders || 0), 0);

        return {
          summary: {
            totalSales,
            totalOrders,
            averageOrderValue: totalOrders > 0 ? totalSales / totalOrders : 0
          }
        };
      }

      // If no stores selected, get all stores data
      const response = await hybridApi.getSalesRange(prevStartDate, prevEndDate);
      return response.data;
    },
    enabled: !!executedFilters,
    refetchOnWindowFocus: false,
  });

  // Reset executing state when query completes
  useEffect(() => {
    if (!isLoading && isExecuting) {
      setIsExecuting(false);
    }
  }, [isLoading, isExecuting]);

  // Scroll to filter panel when data loads successfully
  useEffect(() => {
    if (analyticsData && !isLoading && filterPanelRef.current) {
      console.log('Scrolling to filter panel');
      setTimeout(() => {
        filterPanelRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [analyticsData, isLoading]);

  // Compute overall loading state
  const isLoadingData = isLoading || isExecuting;

  // Calculate metrics from analytics data
  const metrics = useMemo(() => {
    if (!analyticsData) return null;

    const totalSales = analyticsData.summary?.totalSales || 0;
    const totalOrders = analyticsData.summary?.totalOrders || 0;
    const avgOrderValue = analyticsData.summary?.averageOrderValue || 0;
    const avgDailySales = analyticsData.summary?.averageDailySales || 0;

    // Calculate changes from previous period
    const prevSales = previousPeriodData?.summary?.totalSales || 0;
    const prevOrders = previousPeriodData?.summary?.totalOrders || 0;
    const prevAov = previousPeriodData?.summary?.averageOrderValue || 0;

    const salesChange = (totalSales > 0 && prevSales > 0)
      ? ((totalSales - prevSales) / prevSales) * 100
      : 0;
    const ordersChange = (totalOrders > 0 && prevOrders > 0)
      ? ((totalOrders - prevOrders) / prevOrders) * 100
      : 0;
    const aovChange = (avgOrderValue > 0 && prevAov > 0)
      ? ((avgOrderValue - prevAov) / prevAov) * 100
      : 0;

    return {
      totalSales,
      totalOrders,
      avgOrderValue,
      avgDailySales,
      salesChange: Number(salesChange.toFixed(1)),
      ordersChange: Number(ordersChange.toFixed(1)),
      aovChange: Number(aovChange.toFixed(1)),
    };
  }, [analyticsData, previousPeriodData]);

  // Prepare chart data with filters applied
  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (!analyticsData?.dailyBreakdown) return [];

    let filteredData = analyticsData.dailyBreakdown;

    // Data is now filtered by selected stores via API aggregation
    // Brand and source filters are applied to the store list in the UI

    return filteredData.map((day: any) => ({
      date: format(new Date(day.date), 'MMM dd'),
      sales: day.sales,
      orders: day.orders,
      aov: day.averageOrderValue,
    }));
  }, [analyticsData]);

  // Filter stores based on selected brands and sources
  const filteredStores = useMemo(() => {
    if (!storesData) return [];

    let stores = storesData;

    if (filters.selectedBrands.length > 0) {
      stores = stores.filter((s: any) => filters.selectedBrands.includes(s.brand));
    }

    if (filters.selectedSources.length > 0) {
      stores = stores.filter((s: any) => filters.selectedSources.includes(s.source_system));
    }

    return stores;
  }, [storesData, filters.selectedBrands, filters.selectedSources]);

  return (
    <Box sx={{ minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ pt: 4, pb: 3 }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Assessment sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'white' }}>
                  Advanced Analytics
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={showTemplates ? "Hide Report Templates" : "Show Report Templates"}>
                  <IconButton
                    onClick={() => setShowTemplates(!showTemplates)}
                    sx={{
                      color: showTemplates ? 'primary.main' : 'white',
                      '&:hover': { bgcolor: 'rgba(175, 35, 38, 0.1)' }
                    }}
                  >
                    <Assessment />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={() => refetch()}
                    sx={{
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(175, 35, 38, 0.1)' }
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export to CSV">
                  <IconButton
                    onClick={handleExportData}
                    disabled={!analyticsData}
                    sx={{
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(175, 35, 38, 0.1)' }
                    }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
                <Tooltip title={showFilters ? 'Hide Filters' : 'Show Filters'}>
                  <IconButton
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{
                      color: showFilters ? 'primary.main' : 'white',
                      '&:hover': { bgcolor: 'rgba(175, 35, 38, 0.1)' }
                    }}
                  >
                    <FilterList />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Comprehensive reporting with multi-dimensional filtering and visualization
            </Typography>

            {/* Active Filters Display */}
            {(filters.selectedStores.length > 0 ||
              filters.selectedBrands.length > 0 ||
              filters.selectedSources.length > 0) && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {filters.selectedStores.length > 0 && (
                  <Chip
                    label={`${filters.selectedStores.length} Store${filters.selectedStores.length > 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      '& .MuiChip-label': {
                        px: 1.5,
                        overflow: 'visible',
                      },
                    }}
                  />
                )}
                {filters.selectedBrands.map((brand) => (
                  <Chip
                    key={brand}
                    label={brand}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      '& .MuiChip-label': {
                        px: 1.5,
                        overflow: 'visible',
                      },
                    }}
                  />
                ))}
                {filters.selectedSources.map((source) => (
                  <Chip
                    key={source}
                    label={source.toUpperCase()}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{
                      '& .MuiChip-label': {
                        px: 1.5,
                        overflow: 'visible',
                      },
                    }}
                  />
                ))}
                <Chip
                  label={filters.dateRange.preset}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(175, 35, 38, 0.2)',
                    color: 'primary.main',
                    fontWeight: 600,
                    '& .MuiChip-label': {
                      px: 1.5,
                      overflow: 'visible',
                    },
                  }}
                />
              </Box>
            )}
          </motion.div>
        </Box>

        {/* Quick Report Templates */}
        <Fade in={showTemplates}>
          <Box>
            <QuickReportTemplates onTemplateSelect={handleTemplateSelect} />
          </Box>
        </Fade>

        {/* Data Availability Info */}
        {!showTemplates && (
          <Fade in={!showTemplates}>
            <Box sx={{ mb: 3 }}>
              <Paper sx={{ ...glassStyle, p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <InfoOutlined sx={{ color: '#2196f3' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                    Data Availability by POS System
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2, border: '1px solid rgba(76, 175, 80, 0.3)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#4caf50', mb: 1 }}>
                        Speedline (86 stores)
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>✅ Daily sales & trends</Typography></li>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>✅ Meal period analysis (77% coverage)</Typography></li>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>⚠️ Revenue centers (limited data)</Typography></li>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>✅ Payment methods (77% coverage)</Typography></li>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>✅ Product mix (77% coverage)</Typography></li>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2, border: '1px solid rgba(255, 152, 0, 0.3)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ff9800', mb: 1 }}>
                        PAR / Brink (53 stores)
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>✅ Daily sales & trends</Typography></li>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>✅ Meal period analysis (new!)</Typography></li>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>❌ Revenue centers (not available)</Typography></li>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>❌ Payment methods (not available)</Typography></li>
                        <li><Typography variant="body2" sx={{ color: 'white' }}>❌ Product mix (not available)</Typography></li>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Fade>
        )}

        {/* Filter Panel */}
        <Fade in={showFilters}>
          <Box sx={{ mb: 3 }} ref={filterPanelRef}>
            <FilterPanel
              filters={filters}
              onFiltersChange={setFilters}
              stores={filteredStores}
              availableBrands={['Happy\'s Pizza', 'Savvy Sliders']}
              availableSources={['speedline', 'par']}
              onExecuteReport={handleExecuteReport}
            />
          </Box>
        </Fade>

        {/* Error State */}
        {analyticsError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            icon={<ErrorOutline />}
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Failed to load analytics data. Please try again or select different filters.
          </Alert>
        )}

        {/* Empty State - No Report Executed */}
        {!isLoadingData && !analyticsData && !analyticsError && (
          <Paper sx={{ ...glassStyle, p: 6, mb: 4, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <InfoOutlined sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                No Report Generated
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Select a quick report template or configure filters and click "Go" to generate your report.
              </Typography>
            </motion.div>
          </Paper>
        )}

        {/* Empty State - No Data Available */}
        {!isLoadingData && analyticsData && chartData.length === 0 && !analyticsError && (
          <Paper sx={{ ...glassStyle, p: 6, mb: 4, textAlign: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Assessment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                No Data Available
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                There is no sales data for the selected date range and filters. Try selecting a different date range or stores.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowFilters(true)}
                sx={{ mt: 2 }}
              >
                Adjust Filters
              </Button>
            </motion.div>
          </Paper>
        )}

        {/* Key Metrics */}
        {isLoadingData ? (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 4 }} />
              </Grid>
            ))}
          </Grid>
        ) : metrics && chartData.length > 0 ? (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Sales"
                value={formatCurrency(metrics.totalSales)}
                change={metrics.salesChange}
                icon={<TrendingUp />}
                trend={metrics.salesChange >= 0 ? 'up' : 'down'}
                delay={0.1}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Orders"
                value={formatNumber(metrics.totalOrders)}
                change={metrics.ordersChange}
                icon={<Assessment />}
                trend={metrics.ordersChange >= 0 ? 'up' : 'down'}
                delay={0.2}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Order Value"
                value={formatCurrency(metrics.avgOrderValue)}
                change={metrics.aovChange}
                icon={<TrendingUp />}
                trend={metrics.aovChange >= 0 ? 'up' : 'down'}
                delay={0.3}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Daily Sales"
                value={formatCurrency(metrics.avgDailySales)}
                change={metrics.salesChange}
                icon={<Assessment />}
                trend={metrics.salesChange >= 0 ? 'up' : 'down'}
                delay={0.4}
              />
            </Grid>
          </Grid>
        ) : null}

        {/* Quick Insights Panel */}
        {!isLoadingData && analyticsData && metrics && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Paper sx={{ ...glassStyle, mb: 4, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Lightbulb sx={{ fontSize: 32, color: '#ffc107' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Key Insights
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {(() => {
                  const insights: Array<{
                    icon: any;
                    color: string;
                    text: string;
                    type: 'success' | 'warning' | 'info';
                  }> = [];

                  // RANGE MODE INSIGHTS
                  if (!analyticsData.mode || analyticsData.mode === 'range') {
                    // Sales performance vs previous period
                    if (metrics.salesChange !== 0) {
                      const isPositive = metrics.salesChange > 0;
                      insights.push({
                        icon: isPositive ? CheckCircle : Warning,
                        color: isPositive ? '#4caf50' : '#f44336',
                        text: `Sales are ${isPositive ? 'up' : 'down'} ${Math.abs(metrics.salesChange).toFixed(1)}% compared to the previous period (${formatCurrency(Math.abs(metrics.totalSales - (previousPeriodData?.summary?.totalSales || 0)))})`,
                        type: isPositive ? 'success' : 'warning'
                      });
                    }

                    // Best day analysis
                    if (chartData.length > 1) {
                      const bestDay = chartData.reduce((max: ChartDataPoint, day: ChartDataPoint) =>
                        day.sales > max.sales ? day : max, chartData[0]
                      );
                      insights.push({
                        icon: Star,
                        color: '#ffc107',
                        text: `Best performing day: ${bestDay.date} with ${formatCurrency(bestDay.sales)} in sales (${bestDay.orders} orders)`,
                        type: 'success'
                      });
                    }

                    // Average performance
                    if (metrics.avgDailySales > 0) {
                      insights.push({
                        icon: Assessment,
                        color: '#2196f3',
                        text: `Daily average: ${formatCurrency(metrics.avgDailySales)} across ${chartData.length} day${chartData.length !== 1 ? 's' : ''}`,
                        type: 'info'
                      });
                    }
                  }

                  // TREND MODE INSIGHTS
                  if (analyticsData.mode === 'trend' && analyticsData.periods) {
                    // Calculate overall trend
                    const periods = analyticsData.periods;
                    let improvingCount = 0;
                    let decliningCount = 0;

                    for (let i = 1; i < periods.length; i++) {
                      const current = periods[i].data?.summary?.totalSales || 0;
                      const previous = periods[i - 1].data?.summary?.totalSales || 0;
                      if (previous > 0) {
                        const change = ((current - previous) / previous) * 100;
                        if (change >= 3) improvingCount++;
                        else if (change <= -3) decliningCount++;
                      }
                    }

                    const trendDirection = improvingCount > decliningCount ? 'improving' :
                                          decliningCount > improvingCount ? 'declining' : 'stable';

                    insights.push({
                      icon: trendDirection === 'improving' ? TrendingUp :
                            trendDirection === 'declining' ? TrendingDown : Assessment,
                      color: trendDirection === 'improving' ? '#4caf50' :
                             trendDirection === 'declining' ? '#f44336' : '#9e9e9e',
                      text: `Overall trend is ${trendDirection} with ${improvingCount} period${improvingCount !== 1 ? 's' : ''} showing growth`,
                      type: trendDirection === 'improving' ? 'success' :
                            trendDirection === 'declining' ? 'warning' : 'info'
                    });

                    // Best period
                    const bestPeriod = periods.reduce((max: any, p: any) =>
                      (p.data?.summary?.totalSales || 0) > (max.data?.summary?.totalSales || 0) ? p : max,
                      periods[0]
                    );

                    insights.push({
                      icon: Star,
                      color: '#ffc107',
                      text: `Strongest period: ${bestPeriod.label} with ${formatCurrency(bestPeriod.data?.summary?.totalSales || 0)}`,
                      type: 'success'
                    });

                    // Largest change
                    let largestChange = 0;
                    let changeLabel = '';
                    for (let i = 1; i < periods.length; i++) {
                      const current = periods[i].data?.summary?.totalSales || 0;
                      const previous = periods[i - 1].data?.summary?.totalSales || 0;
                      if (previous > 0) {
                        const change = Math.abs(((current - previous) / previous) * 100);
                        if (change > largestChange) {
                          largestChange = change;
                          changeLabel = `${periods[i].label} vs ${periods[i - 1].label}`;
                        }
                      }
                    }

                    if (largestChange > 5) {
                      insights.push({
                        icon: Warning,
                        color: '#ff9800',
                        text: `Largest period-over-period change: ${largestChange.toFixed(1)}% between ${changeLabel}`,
                        type: 'warning'
                      });
                    }
                  }

                  // COMPARISON MODE INSIGHTS
                  if (analyticsData.mode === 'comparison' && analyticsData.comparisons) {
                    const comps = analyticsData.comparisons;

                    // Best comparison
                    const bestComp = comps.reduce((max: any, c: any) =>
                      (c.comparison?.salesChange || 0) > (max.comparison?.salesChange || 0) ? c : max,
                      comps[0]
                    );

                    insights.push({
                      icon: bestComp.comparison.salesChange >= 0 ? TrendingUp : TrendingDown,
                      color: bestComp.comparison.salesChange >= 0 ? '#4caf50' : '#f44336',
                      text: `Best YoY performance: ${bestComp.label} ${bestComp.comparison.salesChange >= 0 ? 'up' : 'down'} ${Math.abs(bestComp.comparison.salesChange).toFixed(1)}% vs last year`,
                      type: bestComp.comparison.salesChange >= 0 ? 'success' : 'warning'
                    });

                    // Worst comparison (if negative)
                    const worstComp = comps.reduce((min: any, c: any) =>
                      (c.comparison?.salesChange || 0) < (min.comparison?.salesChange || 0) ? c : min,
                      comps[0]
                    );

                    if (worstComp.comparison.salesChange < -5) {
                      insights.push({
                        icon: Warning,
                        color: '#f44336',
                        text: `Alert: ${worstComp.label} down ${Math.abs(worstComp.comparison.salesChange).toFixed(1)}% YoY - requires attention`,
                        type: 'warning'
                      });
                    }

                    // Overall YoY summary
                    const positiveCount = comps.filter((c: any) => (c.comparison?.salesChange || 0) > 0).length;
                    insights.push({
                      icon: Assessment,
                      color: '#2196f3',
                      text: `${positiveCount} of ${comps.length} periods showing year-over-year growth`,
                      type: 'info'
                    });
                  }

                  // FILTER-SPECIFIC INSIGHTS
                  // Revenue center insight
                  if (executedFilters?.selectedRevenueCenters &&
                      !executedFilters.selectedRevenueCenters.includes('all')) {
                    const centerNames = {
                      dineIn: 'Dine-In',
                      takeout: 'Takeout',
                      delivery: 'Delivery',
                      curbside: 'Curbside'
                    };
                    const selectedNames = executedFilters.selectedRevenueCenters.map(
                      c => centerNames[c as keyof typeof centerNames] || c
                    ).join(', ');

                    insights.push({
                      icon: Assessment,
                      color: '#2196f3',
                      text: `Analyzing ${selectedNames} revenue center${executedFilters.selectedRevenueCenters.length > 1 ? 's' : ''} - see distribution below`,
                      type: 'info'
                    });
                  }

                  // Meal period insight
                  if (executedFilters?.selectedMealPeriods &&
                      !executedFilters.selectedMealPeriods.includes('allDay')) {
                    const periodNames = {
                      breakfast: 'Breakfast',
                      lunch: 'Lunch',
                      dinner: 'Dinner',
                      lateNight: 'Late Night'
                    };
                    const selectedNames = executedFilters.selectedMealPeriods.map(
                      p => periodNames[p as keyof typeof periodNames] || p
                    ).join(', ');

                    insights.push({
                      icon: Assessment,
                      color: '#2196f3',
                      text: `Daypart analysis for ${selectedNames} - see performance comparison below`,
                      type: 'info'
                    });
                  }

                  return insights.map((insight, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          bgcolor: insight.type === 'success' ? 'rgba(76, 175, 80, 0.05)' :
                                   insight.type === 'warning' ? 'rgba(255, 152, 0, 0.05)' :
                                   'rgba(33, 150, 243, 0.05)',
                          border: `1px solid ${insight.color}33`,
                        }}
                      >
                        <insight.icon sx={{ color: insight.color, fontSize: 24, flexShrink: 0, mt: 0.5 }} />
                        <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                          {insight.text}
                        </Typography>
                      </Box>
                    </Grid>
                  ));
                })()}
              </Grid>
            </Paper>
          </motion.div>
        )}

        {/* Filter-Specific Visualizations */}
        {!isLoadingData && analyticsData && metrics && (
          <>
            {/* Revenue Center Breakdown - Show only when revenue center filters are applied */}
            {executedFilters &&
             executedFilters.selectedRevenueCenters &&
             !executedFilters.selectedRevenueCenters.includes('all') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Paper sx={{ ...glassStyle, mb: 4, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
                    Revenue Center Distribution
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    Sales breakdown by selected revenue centers
                  </Typography>

                  {/* Data availability warning */}
                  {executedFilters.selectedSources.includes('par') && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      <strong>Limited Data:</strong> Revenue center breakdown is not available for PAR stores.
                      Only Speedline stores are shown below.
                    </Alert>
                  )}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={(() => {
                              const centers = executedFilters.selectedRevenueCenters;
                              const colors = {
                                dineIn: '#af2326',
                                takeout: '#4caf50',
                                delivery: '#2196f3',
                                curbside: '#ff9800'
                              };

                              // Use real revenue center breakdown from API
                              const breakdown = analyticsData.revenueCenterBreakdown;

                              if (!breakdown) {
                                // No breakdown data available - show no data message
                                return [{ name: 'No Data Available', value: 1, color: '#9e9e9e' }];
                              }

                              // Use real data from backend
                              const data = centers.map((center) => {
                                const value = center === 'dineIn' ? breakdown.dineIn :
                                              center === 'takeout' ? breakdown.takeout :
                                              center === 'delivery' ? breakdown.delivery :
                                              breakdown.curbside;
                                return {
                                  name: center === 'dineIn' ? 'Dine-In' :
                                        center === 'takeout' ? 'Takeout' :
                                        center === 'delivery' ? 'Delivery' : 'Curbside',
                                  value: value || 0,
                                  color: colors[center as keyof typeof colors] || '#9e9e9e'
                                };
                              }).filter(item => item.value > 0); // Only show centers with data
                              return data.length > 0 ? data : [{ name: 'No Data', value: 1, color: '#9e9e9e' }];
                            })()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {executedFilters.selectedRevenueCenters.map((entry, index) => {
                              const colors = {
                                dineIn: '#af2326',
                                takeout: '#4caf50',
                                delivery: '#2196f3',
                                curbside: '#ff9800'
                              };
                              return (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={colors[entry as keyof typeof colors] || '#9e9e9e'}
                                />
                              );
                            })}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={{
                              backgroundColor: 'rgba(28, 24, 25, 0.98)',
                              border: '1px solid rgba(175, 35, 38, 0.5)',
                              borderRadius: '12px',
                              padding: '12px',
                            }}
                            formatter={(value: any) => formatCurrency(value)}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 4 }}>
                        {executedFilters.selectedRevenueCenters.map((center) => {
                          const colors = {
                            dineIn: '#af2326',
                            takeout: '#4caf50',
                            delivery: '#2196f3',
                            curbside: '#ff9800'
                          };
                          const names = {
                            dineIn: 'Dine-In',
                            takeout: 'Takeout',
                            delivery: 'Delivery',
                            curbside: 'Curbside'
                          };

                          // Use real data from API
                          const breakdown = analyticsData.revenueCenterBreakdown;

                          // Calculate actual value from real data
                          const value = breakdown ? (
                            center === 'dineIn' ? breakdown.dineIn :
                            center === 'takeout' ? breakdown.takeout :
                            center === 'delivery' ? breakdown.delivery :
                            breakdown.curbside
                          ) : 0;

                          // Calculate percentage based on total sales
                          const percentage = breakdown && metrics.totalSales > 0
                            ? ((value / metrics.totalSales) * 100).toFixed(1)
                            : '0.0';

                          return (
                            <Box key={center} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: '4px',
                                  bgcolor: colors[center as keyof typeof colors],
                                  flexShrink: 0
                                }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
                                    {names[center as keyof typeof names]}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {percentage}%
                                  </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ color: colors[center as keyof typeof colors], fontWeight: 700 }}>
                                  {formatCurrency(value)}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(175, 35, 38, 0.05)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      💡 This distribution shows sales across selected revenue centers for the current date range
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            )}

            {/* Meal Period Breakdown - Show only when meal period filters are applied */}
            {executedFilters &&
             executedFilters.selectedMealPeriods &&
             !executedFilters.selectedMealPeriods.includes('allDay') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Paper sx={{ ...glassStyle, mb: 4, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
                    Daypart Performance
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    Sales comparison across selected meal periods
                  </Typography>

                  {/* Data availability note */}
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <strong>New Feature:</strong> Meal period data is now available for both PAR and Speedline stores!
                    PAR data is extracted from order timestamps.
                  </Alert>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={(() => {
                        const periods = executedFilters.selectedMealPeriods;
                        const colors = {
                          breakfast: '#ff9800',
                          lunch: '#4caf50',
                          dinner: '#af2326',
                          lateNight: '#9c27b0'
                        };
                        const names = {
                          breakfast: 'Breakfast',
                          lunch: 'Lunch',
                          dinner: 'Dinner',
                          lateNight: 'Late Night'
                        };

                        // Use real meal period breakdown from API
                        const breakdown = analyticsData.mealPeriodBreakdown;

                        if (!breakdown || typeof breakdown !== 'object') {
                          // No breakdown data available - show no data message
                          return [{ name: 'No Data Available', sales: 1, orders: 0, color: '#9e9e9e' }];
                        }

                        // Use real data from backend
                        const data = periods.map((period) => {
                          const periodData = breakdown[period as keyof typeof breakdown] || { totalSales: 0, orderCount: 0 };
                          const sales = typeof periodData === 'object' ? (periodData.totalSales || 0) : 0;
                          const orders = typeof periodData === 'object' ? (periodData.orderCount || 0) : 0;

                          return {
                            name: names[period as keyof typeof names] || period,
                            sales: sales,
                            orders: orders,
                            color: colors[period as keyof typeof colors] || '#9e9e9e'
                          };
                        }).filter(item => item.sales > 0); // Only show periods with data

                        return data.length > 0 ? data : [{ name: 'No Data', sales: 1, orders: 0, color: '#9e9e9e' }];
                      })()}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis
                        dataKey="name"
                        stroke="#b8b8b8"
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis
                        stroke="#b8b8b8"
                        style={{ fontSize: '12px' }}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'rgba(28, 24, 25, 0.98)',
                          border: '1px solid rgba(175, 35, 38, 0.5)',
                          borderRadius: '12px',
                          padding: '12px',
                        }}
                        formatter={(value: any, name: string) => [
                          name === 'sales' ? formatCurrency(value) : formatNumber(value),
                          name === 'sales' ? 'Sales' : 'Orders'
                        ]}
                      />
                      <Bar dataKey="sales" radius={[8, 8, 0, 0]}>
                        {executedFilters.selectedMealPeriods.map((entry, index) => {
                          const colors = {
                            breakfast: '#ff9800',
                            lunch: '#4caf50',
                            dinner: '#af2326',
                            lateNight: '#9c27b0'
                          };
                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={colors[entry as keyof typeof colors] || '#9e9e9e'}
                            />
                          );
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 3, display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {executedFilters.selectedMealPeriods.map((period) => {
                      const colors = {
                        breakfast: '#ff9800',
                        lunch: '#4caf50',
                        dinner: '#af2326',
                        lateNight: '#9c27b0'
                      };
                      const names = {
                        breakfast: 'Breakfast (6am-11am)',
                        lunch: 'Lunch (11am-3pm)',
                        dinner: 'Dinner (3pm-10pm)',
                        lateNight: 'Late Night (10pm-2am)'
                      };

                      return (
                        <Chip
                          key={period}
                          label={names[period as keyof typeof names]}
                          sx={{
                            bgcolor: `${colors[period as keyof typeof colors]}33`,
                            color: colors[period as keyof typeof colors],
                            fontWeight: 600,
                            border: `1px solid ${colors[period as keyof typeof colors]}66`,
                          }}
                        />
                      );
                    })}
                  </Box>
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(175, 35, 38, 0.05)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      💡 Daypart analysis helps identify peak hours and optimize staffing & inventory
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            )}
          </>
        )}

        {/* Comparison Results Table */}
        {!isLoadingData && analyticsData && analyticsData.mode === 'comparison' && analyticsData.comparisons && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Paper sx={{ ...glassStyle, mb: 4, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Year-over-Year Comparison
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  Comparing selected periods to the same periods last year
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 100 }}>Period</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 120 }}>Date Range</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 120 }}>Current Year</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 120 }}>Last Year</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 100 }}>Difference</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 100 }}>Change %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.comparisons.map((comp: any, index: number) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': {
                            bgcolor: 'rgba(175, 35, 38, 0.08)',
                            transition: 'all 0.2s ease',
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: 'white', fontSize: '0.9rem' }}>
                          {comp.label}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                              {format(new Date(comp.currentPeriod.startDate), 'MMM dd')} - {format(new Date(comp.currentPeriod.endDate), 'MMM dd, yyyy')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: '#4caf50' }}>
                          {formatCurrency(comp.currentPeriod.totalSales || 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                          {formatCurrency(comp.lastYearPeriod.totalSales || 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: comp.comparison.salesDiff >= 0 ? '#4caf50' : '#f44336',
                              fontWeight: 600,
                            }}
                          >
                            {comp.comparison.salesDiff >= 0 ? '+' : ''}
                            {formatCurrency(Math.abs(comp.comparison.salesDiff))}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${comp.comparison.salesChange >= 0 ? '+' : ''}${comp.comparison.salesChange.toFixed(1)}%`}
                            size="small"
                            icon={comp.comparison.salesChange >= 0 ? <TrendingUp /> : <TrendingDown />}
                            sx={{
                              bgcolor: comp.comparison.salesChange >= 0
                                ? 'rgba(76, 175, 80, 0.2)'
                                : 'rgba(244, 67, 54, 0.2)',
                              color: comp.comparison.salesChange >= 0
                                ? '#4caf50'
                                : '#f44336',
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              border: `1px solid ${comp.comparison.salesChange >= 0 ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'rgba(175, 35, 38, 0.05)',
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  💡 Positive changes are shown in green, negative changes in red. All comparisons are vs. the same period last year (365 days prior).
                </Typography>
              </Box>
            </Paper>
          </motion.div>
        )}

        {/* Trend Results Table */}
        {!isLoadingData && analyticsData && analyticsData.mode === 'trend' && analyticsData.periods && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Paper sx={{ ...glassStyle, mb: 4, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Period Trend Analysis
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  Showing {analyticsData.periods.length} {executedFilters?.trendPeriodType?.toLowerCase() || 'periods'} with period-over-period comparison
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 120 }}>Period</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 140 }}>Date Range</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 120 }}>Sales</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 100 }}>Orders</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 100 }}>Avg Order</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 120 }}>vs Previous</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.875rem', minWidth: 100 }}>Trend</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.periods.map((period: any, index: number) => {
                      const periodSales = period.data?.summary?.totalSales || 0;
                      const periodOrders = period.data?.summary?.totalOrders || 0;
                      const periodAov = period.data?.summary?.averageOrderValue || 0;

                      // Calculate change from previous period
                      let salesChange = 0;
                      let ordersChange = 0;
                      let salesDiff = 0;
                      if (index > 0) {
                        const prevPeriodSales = analyticsData.periods[index - 1].data?.summary?.totalSales || 0;
                        const prevPeriodOrders = analyticsData.periods[index - 1].data?.summary?.totalOrders || 0;

                        if (prevPeriodSales > 0) {
                          salesChange = ((periodSales - prevPeriodSales) / prevPeriodSales) * 100;
                          salesDiff = periodSales - prevPeriodSales;
                        }
                        if (prevPeriodOrders > 0) {
                          ordersChange = ((periodOrders - prevPeriodOrders) / prevPeriodOrders) * 100;
                        }
                      }

                      // Determine trend (improving, stable, declining)
                      let trendLabel = 'Stable';
                      let trendColor = '#9e9e9e';
                      let TrendIcon = TrendingUp;

                      if (Math.abs(salesChange) < 3) {
                        trendLabel = 'Stable';
                        trendColor = '#9e9e9e';
                        TrendIcon = TrendingUp;
                      } else if (salesChange >= 3) {
                        trendLabel = 'Improving';
                        trendColor = '#4caf50';
                        TrendIcon = TrendingUp;
                      } else {
                        trendLabel = 'Declining';
                        trendColor = '#f44336';
                        TrendIcon = TrendingDown;
                      }

                      return (
                        <TableRow
                          key={index}
                          sx={{
                            '&:hover': {
                              bgcolor: 'rgba(175, 35, 38, 0.08)',
                              transition: 'all 0.2s ease',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: 'white', fontSize: '0.9rem' }}>
                            {period.label}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            <Box>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                {period.startDate === period.endDate
                                  ? format(new Date(period.startDate), 'MMM dd, yyyy')
                                  : `${format(new Date(period.startDate), 'MMM dd')} - ${format(new Date(period.endDate), 'MMM dd, yyyy')}`
                                }
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#4caf50' }}>
                            {formatCurrency(periodSales)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {formatNumber(periodOrders)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            {formatCurrency(periodAov)}
                          </TableCell>
                          <TableCell align="right">
                            {index > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                <Chip
                                  label={`${salesChange >= 0 ? '+' : ''}${salesChange.toFixed(1)}%`}
                                  size="small"
                                  icon={salesChange >= 0 ? <TrendingUp /> : <TrendingDown />}
                                  sx={{
                                    bgcolor: salesChange >= 0
                                      ? 'rgba(76, 175, 80, 0.2)'
                                      : 'rgba(244, 67, 54, 0.2)',
                                    color: salesChange >= 0
                                      ? '#4caf50'
                                      : '#f44336',
                                    fontWeight: 700,
                                    fontSize: '0.875rem',
                                    border: `1px solid ${salesChange >= 0 ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: salesChange >= 0 ? '#4caf50' : '#f44336',
                                    fontWeight: 600
                                  }}
                                >
                                  {salesChange >= 0 ? '+' : ''}{formatCurrency(Math.abs(salesDiff))}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {index > 0 ? (
                              <Chip
                                label={trendLabel}
                                size="small"
                                icon={<TrendIcon />}
                                sx={{
                                  bgcolor: `${trendColor}33`,
                                  color: trendColor,
                                  fontWeight: 700,
                                  fontSize: '0.8rem',
                                  border: `1px solid ${trendColor}66`,
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                Baseline
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'rgba(175, 35, 38, 0.05)',
                  borderTop: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  💡 Trend indicators: <strong style={{ color: '#4caf50' }}>Improving</strong> (+3% or more), <strong style={{ color: '#9e9e9e' }}>Stable</strong> (±3%), <strong style={{ color: '#f44336' }}>Declining</strong> (-3% or more)
                </Typography>
              </Box>
            </Paper>
          </motion.div>
        )}

        {/* Charts */}
        {(isLoadingData || (chartData.length > 0 && !analyticsError)) && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} lg={8}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Paper sx={{ ...glassStyle, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
                    Sales Trend
                  </Typography>
                  {isLoadingData ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={chartData}>
                        <defs>
                          <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#af2326" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#af2326" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis
                          dataKey="date"
                          stroke="#b8b8b8"
                          style={{ fontSize: '12px' }}
                          angle={chartData.length > 10 ? -45 : 0}
                          textAnchor={chartData.length > 10 ? 'end' : 'middle'}
                          height={chartData.length > 10 ? 60 : 30}
                        />
                        <YAxis
                          stroke="#b8b8b8"
                          style={{ fontSize: '12px' }}
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: 'rgba(28, 24, 25, 0.98)',
                            border: '1px solid rgba(175, 35, 38, 0.5)',
                            borderRadius: '12px',
                            padding: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          }}
                          labelStyle={{ color: '#fff', fontWeight: 600, marginBottom: '8px' }}
                          formatter={(value: any) => [formatCurrency(value), 'Sales']}
                        />
                        <Area
                          type="monotone"
                          dataKey="sales"
                          stroke="#af2326"
                          strokeWidth={3}
                          fill="url(#salesGradient)"
                          animationDuration={1000}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </Paper>
              </motion.div>
            </Grid>

            <Grid item xs={12} lg={4}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Paper sx={{ ...glassStyle, p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
                    Order Volume
                  </Typography>
                  {isLoadingData ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis
                          dataKey="date"
                          stroke="#b8b8b8"
                          style={{ fontSize: '12px' }}
                          angle={chartData.length > 7 ? -45 : 0}
                          textAnchor={chartData.length > 7 ? 'end' : 'middle'}
                          height={chartData.length > 7 ? 60 : 30}
                        />
                        <YAxis
                          stroke="#b8b8b8"
                          style={{ fontSize: '12px' }}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: 'rgba(28, 24, 25, 0.98)',
                            border: '1px solid rgba(175, 35, 38, 0.5)',
                            borderRadius: '12px',
                            padding: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          }}
                          labelStyle={{ color: '#fff', fontWeight: 600, marginBottom: '8px' }}
                          formatter={(value: any) => [value, 'Orders']}
                        />
                        <Bar
                          dataKey="orders"
                          fill="#af2326"
                          radius={[8, 8, 0, 0]}
                          animationDuration={1000}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        )}

        {/* Data Table */}
        {(isLoadingData || (chartData.length > 0 && !analyticsError)) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Paper sx={{ ...glassStyle, overflow: 'hidden' }}>
              <Box
                sx={{
                  p: 3,
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Detailed Breakdown
                </Typography>
                <Chip
                  label={`${chartData.length} day${chartData.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(175, 35, 38, 0.2)',
                    color: 'primary.main',
                    fontWeight: 600,
                  }}
                />
              </Box>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Date</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Sales</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Orders</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Avg Order Value</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingData ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton /></TableCell>
                          <TableCell><Skeleton /></TableCell>
                          <TableCell><Skeleton /></TableCell>
                          <TableCell><Skeleton /></TableCell>
                          <TableCell><Skeleton /></TableCell>
                        </TableRow>
                      ))
                    ) : (
                      chartData.map((row: ChartDataPoint, index: number) => (
                        <TableRow
                          key={index}
                          sx={{
                            '&:hover': {
                              bgcolor: 'rgba(175, 35, 38, 0.08)',
                              transition: 'all 0.2s ease',
                            },
                            '&:last-child td': {
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: 'white' }}>{row.date}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: '#4caf50' }}>
                            {formatCurrency(row.sales)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>{formatNumber(row.orders)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>{formatCurrency(row.aov)}</TableCell>
                          <TableCell align="right">
                            {index > 0 && chartData[index - 1] && chartData[index - 1].sales > 0 ? (
                              <Chip
                                label={`${((row.sales - chartData[index - 1].sales) / chartData[index - 1].sales * 100).toFixed(1)}%`}
                                size="small"
                                icon={row.sales >= chartData[index - 1].sales ? <TrendingUp /> : <TrendingDown />}
                                sx={{
                                  bgcolor: row.sales >= chartData[index - 1].sales
                                    ? 'rgba(76, 175, 80, 0.2)'
                                    : 'rgba(244, 67, 54, 0.2)',
                                  color: row.sales >= chartData[index - 1].sales
                                    ? '#4caf50'
                                    : '#f44336',
                                  fontWeight: 600,
                                  border: `1px solid ${row.sales >= chartData[index - 1].sales ? 'rgba(76, 175, 80, 0.4)' : 'rgba(244, 67, 54, 0.4)'}`,
                                }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </motion.div>
        )}
      </Container>
    </Box>
  );
};

export default AdvancedAnalytics;
